import { ReactNode, useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { FiMail } from 'react-icons/fi';
import { toast } from 'react-toastify';
import PageHeader from '../components/PageHeader';
import Button from '../components/Button';
import Field from '../components/Field';
import IntegrationCard from '../components/IntegrationCard';
import { gmailService, GmailMailbox } from '../services/gmailService';
import { idpService } from '../services/idpService';
import { setDmsEnabled, useDmsEnabled } from '../utils/dmsIntegration';
import { apiErrorMessage } from '../utils/apiError';
import { inputClass } from '../utils/formStyles';

type Panel = 'enable' | 'configure' | null;

const GmailAddressField = ({
  id,
  email,
  onChange,
  action,
}: {
  id: string;
  email: string;
  onChange: (value: string) => void;
  action: ReactNode;
}) => (
  <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
    <Field htmlFor={id} label="Gmail address" className="flex-1">
      <input
        id={id}
        type="email"
        value={email}
        onChange={(event) => onChange(event.target.value)}
        placeholder="name@gmail.com"
        className={inputClass}
      />
    </Field>
    {action}
  </div>
);

/** The switch is on as soon as it is flipped. A connected mailbox keeps it on after reload. */
const GmailIntegration = () => {
  const location = useLocation();
  const [mailboxes, setMailboxes] = useState<GmailMailbox[]>([]);
  const [email, setEmail] = useState('');
  const [on, setOn] = useState<boolean | null>(null);
  const [connecting, setConnecting] = useState(false);
  const [panel, setPanel] = useState<Panel>(
    (location.state as { configure?: string } | null)?.configure === 'gmail' ? 'configure' : null
  );

  const load = useCallback(async () => {
    try {
      const rows = await gmailService.list();
      setMailboxes(rows);
      setOn(rows.length > 0);
    } catch {
      toast.error('Could not load mailboxes.');
      setOn(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (on && mailboxes.length === 0 && panel === 'configure') setPanel(null);
  }, [on, mailboxes.length, panel]);

  const connect = async () => {
    const address = email.trim();
    if (!address.includes('@')) {
      toast.error('Enter the Gmail address to connect.');
      return;
    }
    setConnecting(true);
    try {
      window.location.href = await gmailService.authorizeUrl(address);
    } catch {
      toast.error('Gmail OAuth is not configured on TaskService.');
      setConnecting(false);
    }
  };

  const disconnect = async (mailbox: GmailMailbox) => {
    await gmailService.disconnect(mailbox.mailboxId);
    setMailboxes((current) => current.filter((item) => item.mailboxId !== mailbox.mailboxId));
  };

  const turnOn = () => {
    setOn(true);
    if (mailboxes.length === 0) setPanel('enable');
  };

  const turnOff = async () => {
    setOn(false);
    setPanel(null);
    for (const mailbox of mailboxes) {
      try {
        await disconnect(mailbox);
      } catch {
        toast.error('Could not disconnect that mailbox.');
        setOn(true);
        return;
      }
    }
  };

  const connectAction = (
    <Button variant="primary" size="sm" icon={<FiMail />} loading={connecting} onClick={connect}>
      Connect Gmail
    </Button>
  );

  return (
    <IntegrationCard
      title="Gmail"
      description="Mail that arrives in a connected mailbox becomes an enquiry."
      enabled={on}
      busy={connecting}
      onToggle={(next) => { if (next) turnOn(); else void turnOff(); }}
      onConfigure={() => setPanel((current) => (current === 'configure' ? null : 'configure'))}
      panel={panel === 'enable' && mailboxes.length === 0 ? (
        <GmailAddressField id="gmail-enable-email" email={email} onChange={setEmail} action={connectAction} />
      ) : panel === 'configure' && mailboxes.length > 0 ? (
        <div className="space-y-4">
          <ul className="divide-y divide-line border border-line rounded-control">
            {mailboxes.map((mailbox) => (
              <li key={mailbox.mailboxId} className="flex items-center justify-between gap-4 px-4 py-3">
                <div className="min-w-0">
                  <p className="font-medium text-ink truncate">{mailbox.email}</p>
                  <p className="text-meta text-ink-subtle">Connected {new Date(mailbox.connectedAt).toLocaleString()}</p>
                </div>
                <Button variant="danger" size="sm" onClick={() => disconnect(mailbox).catch(() => toast.error('Could not disconnect that mailbox.'))}>Disconnect</Button>
              </li>
            ))}
          </ul>
          <GmailAddressField id="gmail-configure-email" email={email} onChange={setEmail} action={connectAction} />
        </div>
      ) : undefined}
    />
  );
};

const IdpIntegration = () => {
  const [on, setOn] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    idpService.getIntegration()
      .then((settings) => setOn(settings.enabled))
      .catch(() => setOn(false));
  }, []);

  const toggle = async (next: boolean) => {
    setBusy(true);
    try {
      const settings = await idpService.setEnabled(next);
      setOn(settings.enabled);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not update IDP.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <IntegrationCard
      title="IDP"
      description="Extracted emails wait in Review until someone accepts them."
      enabled={on}
      busy={busy}
      onToggle={(next) => { void toggle(next); }}
    />
  );
};

const DmsIntegration = () => {
  const on = useDmsEnabled();

  return (
    <IntegrationCard
      title="DMS"
      description="Document Management system"
      enabled={on}
      onToggle={setDmsEnabled}
    />
  );
};

/**
 * Admin integration catalog. Each integration is a card; add the next one beside Gmail.
 */
const Integrations = () => (
  <div className="p-8 lg:p-10">
    <PageHeader
      title="Integration"
      subtitle="Enable an integration, then configure it."
    />
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">
      <GmailIntegration />
      <DmsIntegration />
      <IdpIntegration />
    </div>
  </div>
);

export default Integrations;
